import { useToast } from '@chakra-ui/react';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import sadFaceIcon from '../../../../../assets/images/sad_face-icon.png';
import copyIcon from '../../../../../assets/svgs/copy-icon.svg';
import eyeIcon from '../../../../../assets/svgs/eye-icon.svg';
import fireIcon from '../../../../../assets/svgs/fire-icon.svg';
import { CustomIcon } from '../../../../../components/widgets/CustomIcon';
import { TooltipComponent } from '../../../../../components/widgets/Tooltip';
import { useMediaQuery } from '../../../../../hooks/useMediaQuery';
import { useNotificationContext } from '../context/NotificationContext';
import { NotificationTableProps } from '../types/notificationTypes';
import { getDateFormatted } from '../utils/getDateFormatted';
import { NotificationTableFooter } from './NotificationTableFooter';
import { NotificationTableHeader } from './NotificationTableHeader';

export const NotificationTable = ({
  isLoading,
  data,
  totalData,
  headersIcons,
  buttonsMobile
}: NotificationTableProps) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const {
    notificationsFiltered,
    selectAll,
    selectedItems,
    setSelectAll,
    setSelectedItems,
    resetCheckboxes,
    currentPage
  } = useNotificationContext();

  const navigate = useNavigate();
  const toast = useToast();

  const [expandedRows, setExpandedRows] = useState([]);

  const title = 'Acompanhamento de notificações';
  const subtitle = `Quantidade total de mensagens: ${totalData}`;

  const notificationObj = {
    'novo lead': 'Você recebeu um novo lead.',
    'novos leads': 'Você recebeu novos leads.',
    'nova venda': 'Uma venda acaba de ser concluída.',
    'novo agendamento': 'Você tem um agendamento',
    'novos agendamentos': 'Você tem novos agendamentos'
  };

  const linePerPage = [5, 10, 15, 20];

  const toggleSelectAll = () => {
    const updatedSelectedItems = new Set(selectedItems);

    if (!selectAll) {
      notificationsFiltered.forEach((item) =>
        updatedSelectedItems.add(item.id)
      );
    } else {
      notificationsFiltered.forEach((item) => {
        if (updatedSelectedItems.has(item.id)) {
          updatedSelectedItems.delete(item.id);
        }
      });
    }

    setSelectedItems(updatedSelectedItems);

    setSelectAll(!selectAll);
  };

  const toggleSelectItem = (itemId: string) => {
    const updatedSelectedItems = new Set(selectedItems);

    if (updatedSelectedItems.has(itemId)) {
      updatedSelectedItems.delete(itemId);
    } else {
      updatedSelectedItems.add(itemId);
    }
    setSelectedItems(updatedSelectedItems);
  };

  const toggleRow = (id: string) => {
    const isRowExpanded = expandedRows.includes(id);
    const newExpandedRows = isRowExpanded
      ? expandedRows.filter((rowId) => rowId !== id)
      : [...expandedRows, id];
    setExpandedRows(newExpandedRows);
  };

  const copyElement = (text: string, element: string) => {
    navigator.clipboard.writeText(text);
    toast({
      position: 'top',
      description: `${element} copiado com sucesso.`,
      status: 'success',
      duration: 7000,
      isClosable: true
    });
  };

  const formatPhoneNumber = (phoneNumber: string) => {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');

    const isCellPhone = cleaned.length === 11 && cleaned.charAt(2) === '9';

    if (isCellPhone) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
  };

  useEffect(() => {
    setSelectedItems(new Set());
    resetCheckboxes();
  }, [currentPage]);

  return !isLoading && notificationsFiltered.length >= 1 ? (
    <div className="tw-bg-white tw-text-[#001E64] tw-shadow-[0px_0px_5px_rgba(50,50,50,0.25)] tw-rounded-xl">
      <NotificationTableHeader
        title={title}
        subtitle={subtitle}
        data={data}
        headersIcons={headersIcons}
      />
      <div className="">
        <div
          style={{
            maxHeight: isMobile && '500px',
            overflow: isMobile && 'auto'
          }}
          className="tw-px-4"
        >
          <table
            className="tw-w-full tw-text-[0.875rem] tw-relative"
            cellPadding="0"
            cellSpacing="0"
          >
            <thead>
              <tr style={{ display: isMobile && 'none' }}>
                <th className="tw-text-left tw-pb-3 tw-flex tw-gap-12 tw-px-7">
                  <input
                    checked={selectAll}
                    type="checkbox"
                    className="tw-h-5 tw-w-5"
                    onChange={toggleSelectAll}
                  />
                  <span>Mensagem</span>
                </th>
                <th className="tw-text-left tw-px-4 tw-pb-3">Data/hora</th>
                <th
                  className="tw-text-center tw-pb-3 tw-pl-10"
                  style={{ display: isMobile && 'none' }}
                >
                  Status mensagens
                </th>
              </tr>
            </thead>
            <tbody>
              {notificationsFiltered.map((item, index) => {
                return (
                  <Fragment key={item.id}>
                    <tr
                      style={{
                        cursor: !expandedRows.includes(item.id) && 'pointer',
                        borderBottom:
                          !expandedRows.includes(item.id) &&
                          index + 1 !== notificationsFiltered.length
                            ? '0.63rem solid white'
                            : ''
                      }}
                      onClick={() => {
                        toggleRow(item.id);
                      }}
                    >
                      <td>
                        <div
                          style={{
                            borderLeft: `4px solid ${
                              item?.lida ? '#001E64' : '#FF5000'
                            }`,
                            background: index % 2 ? '#D3DEED' : '#F4F4F4',
                            borderRadius:
                              isMobile && !expandedRows.includes(item.id)
                                ? '10px'
                                : isMobile && expandedRows.includes(item.id)
                                ? '10px 10px 0 0'
                                : !expandedRows.includes(item.id)
                                ? '10px 0 0 10px'
                                : '10px 0 0 0'
                          }}
                          className="tw-whitespace-nowrap"
                        >
                          <div className="tw-relative tw-rounded-lg md:tw-rounded-none md:tw-rounded-l-lg tw-py-4 tw-px-6 md:tw-flex tw-gap-12">
                            <input
                              type="checkbox"
                              style={{
                                position: isMobile ? 'absolute' : null
                              }}
                              className="tw-h-5 tw-w-5 tw-right-3.5 tw-top-3.5"
                              onChange={() => toggleSelectItem(item.id)}
                              checked={selectedItems.has(item.id)}
                            />
                            <div className="tw-flex tw-gap-4">
                              <span className="tw-hidden md:tw-flex">
                                {item?.assunto}
                              </span>
                              {expandedRows.includes(item.id) && (
                                <div className="tw-flex tw-gap-3 tw-cursor-pointer">
                                  {headersIcons.map((icon, index) => {
                                    return (
                                      <TooltipComponent
                                        tooltip={icon.tolltip}
                                        key={index}
                                      >
                                        <div
                                          onClick={() => {
                                            const idArray = [];
                                            idArray.push(item?.id);
                                            icon.action(idArray);
                                          }}
                                        >
                                          <CustomIcon
                                            src={icon.icon}
                                            width={20}
                                            height={20}
                                          />
                                        </div>
                                      </TooltipComponent>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            <div
                              className="tw-py-2 tw-flex"
                              style={{
                                display: isMobile ? 'flex' : 'none'
                              }}
                            >
                              <p className="tw-whitespace-nowrap">
                                Mensagem -{' '}
                              </p>
                              &nbsp;
                              <strong className="tw-underline">
                                {
                                  notificationObj[
                                    String(item?.assunto).toLowerCase()
                                  ]
                                }
                              </strong>
                            </div>
                            <div
                              className="tw-py-2 tw-flex tw-bg-red"
                              style={{
                                display: isMobile ? 'flex' : 'none'
                              }}
                            >
                              Data/hora - &nbsp;
                              <strong>
                                {getDateFormatted(item?.updated_at).date} &nbsp;
                                {getDateFormatted(item?.updated_at).time}
                              </strong>
                            </div>
                            <div
                              className="tw-py-2 tw-flex tw-items-center"
                              style={{ display: isMobile ? 'flex' : 'none' }}
                            >
                              Status mensagens - &nbsp;
                              <div
                                className="tw-text-white tw-p-1.5 tw-rounded-lg"
                                style={{
                                  background: item?.lida ? '#001E64' : '#FF5000'
                                }}
                              >
                                {item.lida ? 'Lida' : 'Não lida'}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          display: isMobile && 'none'
                        }}
                      >
                        <div
                          className="tw-py-4 tw-px-6 tw-whitespace-nowrap"
                          style={{
                            background: index % 2 ? '#D3DEED' : '#F4F4F4'
                          }}
                        >
                          {getDateFormatted(item?.updated_at).date} &nbsp;
                          {getDateFormatted(item?.updated_at).time}
                        </div>
                      </td>
                      <td
                        className="tw-w-[25%] tw-text-center"
                        style={{
                          display: isMobile && 'none'
                        }}
                      >
                        <div
                          className="tw-rounded-r-lg tw-py-4 tw-px-6 tw-whitespace-nowrap"
                          style={{
                            background: index % 2 ? '#D3DEED' : '#F4F4F4',
                            borderRadius:
                              isMobile && !expandedRows.includes(item.id)
                                ? '10px'
                                : isMobile && expandedRows.includes(item.id)
                                ? '10px 10px 0 0'
                                : !expandedRows.includes(item.id)
                                ? '0 10px 10px 0'
                                : '0 10px 0 0'
                          }}
                        >
                          <span
                            className="tw-text-white tw-p-1.5 tw-rounded-lg"
                            style={{
                              background: item?.lida ? '#001E64' : '#FF5000'
                            }}
                          >
                            {item.lida ? 'Lida' : 'Não lida'}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {expandedRows.includes(item.id) && (
                      <tr
                        style={{
                          borderBottom:
                            index + 1 !== notificationsFiltered.length
                              ? '0.63rem solid white'
                              : ''
                        }}
                      >
                        <td colSpan={3}>
                          <div
                            style={{
                              display: isMobile && 'none',
                              borderLeft: `4px solid ${
                                item?.lida ? '#001E64' : '#FF5000'
                              }`,
                              background: index % 2 ? '#D3DEED' : '#F4F4F4'
                            }}
                            className="tw-w-full tw-py-2"
                          >
                            <hr className="tw-bg-[#a8a7a7] tw-h-[1px] tw-mx-5" />
                          </div>
                          <div
                            style={{
                              borderLeft: `4px solid ${
                                item?.lida ? '#001E64' : '#FF5000'
                              }`,
                              background: index % 2 ? '#D3DEED' : '#F4F4F4',
                              borderRadius:
                                isMobile && !expandedRows.includes(item.id)
                                  ? '10px'
                                  : isMobile && expandedRows.includes(item.id)
                                  ? '0 0 10px 10px'
                                  : !expandedRows.includes(item.id)
                                  ? '10px 0 0 10px'
                                  : '0 0 10px 10px'
                            }}
                            className="lg:tw-flex tw-justify-between"
                          >
                            {item?.conteudo.quantidade <= 1 ? (
                              <div className="tw-py-4 tw-px-6 tw-w-full lg:tw-max-w-[75%]">
                                <div className="tw-flex tw-gap-2 tw-items-center">
                                  <strong className="tw-text-black">
                                    {item?.conteudo.nome}
                                  </strong>
                                  {item?.conteudo.leadHot && (
                                    <CustomIcon src={fireIcon} />
                                  )}
                                </div>
                                <div className="tw-flex tw-gap-12 tw-pt-3">
                                  <span
                                    style={{
                                      visibility: item?.conteudo.email
                                        ? 'visible'
                                        : 'hidden'
                                    }}
                                    className="tw-flex tw-gap-2 tw-items-center"
                                    onClick={() =>
                                      copyElement(item?.conteudo.email, 'email')
                                    }
                                  >
                                    <CustomIcon src={copyIcon} />
                                    {item?.conteudo.email}
                                  </span>
                                  <div className="tw-flex tw-flex-col tw-gap-2 tw-whitespace-nowrap">
                                    <span
                                      style={{
                                        visibility: item?.conteudo.telefone
                                          ? 'visible'
                                          : 'hidden'
                                      }}
                                      className="tw-flex tw-gap-2 tw-items-center"
                                      onClick={() =>
                                        copyElement(
                                          item?.conteudo.telefone,
                                          'telefone'
                                        )
                                      }
                                    >
                                      <CustomIcon src={copyIcon} />
                                      {formatPhoneNumber(
                                        item?.conteudo.telefone
                                      )}
                                    </span>
                                    <span
                                      style={{
                                        visibility: item?.conteudo.celular
                                          ? 'visible'
                                          : 'hidden'
                                      }}
                                      className="tw-flex tw-gap-2 tw-items-center"
                                      onClick={() =>
                                        copyElement(
                                          item?.conteudo.celular,
                                          'celular'
                                        )
                                      }
                                      onKeyUp={() => {
                                        //
                                      }}
                                    >
                                      <CustomIcon src={copyIcon} />
                                      {formatPhoneNumber(
                                        item?.conteudo.celular
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="tw-py-4 tw-px-6 lg:tw-flex tw-justify-between tw-gap-12 tw-w-full">
                                <p className="tw-flex lg:tw-items-center lg:tw-text-center">
                                  {item?.conteudo.mensagem}
                                </p>
                                <div className=" tw-mt-6 lg:tw-mt-0 tw-flex tw-gap-1 tw-items-start">
                                  {item?.conteudo.campanhas && (
                                    <p
                                      style={{
                                        display: !isMobile && 'none'
                                      }}
                                      className="tw-font-bold tw-text-black tw-mb-2"
                                    >
                                      Campanha:
                                    </p>
                                  )}
                                  <div className="tw-flex lg:tw-flex-col tw-gap-3">
                                    {Array.isArray(item?.conteudo.campanhas) &&
                                      item?.conteudo?.campanhas.every(
                                        (campanha) => campanha !== null
                                      ) &&
                                      item?.conteudo.campanhas.map(
                                        (campanha, index) => (
                                          <div
                                            key={index}
                                            className="tw-flex tw-gap-2"
                                          >
                                            <p
                                              style={{
                                                display: isMobile && 'none'
                                              }}
                                              className="tw-font-bold tw-text-black"
                                            >
                                              Campanha
                                            </p>

                                            <div className="tw-bg-[#FF5500] tw-py-1 tw-px-2 tw-text-[0.75rem] tw-rounded-full tw-text-white tw-font-bold tw-flex tw-items-center tw-justify-center tw-whitespace-nowrap">
                                              {campanha}
                                            </div>
                                          </div>
                                        )
                                      )}
                                  </div>
                                </div>
                              </div>
                            )}
                            <div className="lg:tw-flex tw-gap-1 tw-items-start tw-py-4 tw-px-6">
                              {item?.conteudo.produtos && (
                                <p
                                  style={{
                                    display: !isMobile && 'none'
                                  }}
                                  className="tw-font-bold tw-text-black tw-mb-2"
                                >
                                  Produto:
                                </p>
                              )}
                              <div className="tw-flex lg:tw-flex-col tw-gap-3">
                                {Array.isArray(item?.conteudo.produtos) &&
                                  item?.conteudo?.produtos.every(
                                    (produto) => produto !== null
                                  ) &&
                                  item?.conteudo.produtos.map(
                                    (produto, index) => (
                                      <div
                                        key={index}
                                        className="tw-flex tw-gap-2"
                                      >
                                        <p
                                          style={{
                                            display: isMobile && 'none'
                                          }}
                                          className="tw-font-bold tw-text-black"
                                        >
                                          Produto
                                        </p>

                                        <div className="tw-bg-[#FF5500] tw-py-1 tw-px-2 tw-text-[0.75rem] tw-rounded-full tw-text-white tw-font-bold tw-flex tw-items-center tw-justify-center tw-whitespace-nowrap">
                                          {produto}
                                        </div>
                                      </div>
                                    )
                                  )}
                              </div>
                            </div>
                            <div className="tw-py-4 tw-px-6 tw-flex tw-flex-col tw-items-center">
                              <button
                                className="tw-bg-[#FF5500] tw-rounded-full tw-font-semibold tw-text-white tw-py-2 tw-px-4 tw-whitespace-nowrap tw-flex tw-gap-2 tw-items-center tw-justify-center tw-w-full lg:tw-max-w-[13rem]"
                                onClick={() => {
                                  const url =
                                    item?.conteudo.quantidade <= 1
                                      ? `/corretor/leads/${item?.id_lead}`
                                      : '/corretor/leads';
                                  navigate(url);
                                }}
                              >
                                <CustomIcon src={eyeIcon} width={16} />
                                {item?.conteudo.quantidade <= 1
                                  ? 'Ver Detalhes do Lead'
                                  : 'Ver no Painel de Gestão'}
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="md:tw-hidden">
          <hr className="tw-mt-5" />
          <div className="tw-p-4">
            {buttonsMobile.map((item, index) => {
              return (
                <div
                  key={index}
                  style={{
                    marginBottom: index !== buttonsMobile.length && '0.63rem'
                  }}
                >
                  <button
                    disabled={item.disable}
                    style={{
                      color: item.border,
                      borderColor: item.border,
                      cursor: item.disable ? 'not-allowed' : 'pointer',
                      opacity: item.disable && '0.5'
                    }}
                    className="md:tw-hidden tw-border tw-font-bold tw-px-11 tw-h-10 tw-rounded-full tw-w-full tw-flex tw-items-center tw-justify-center"
                    onClick={() => {
                      const idsArray = Array.from(selectedItems);
                      item.action(idsArray);
                    }}
                  >
                    <div className="tw-flex tw-gap-2">
                      <span className="tw-flex tw-items-center">
                        {item.icon}
                      </span>
                      <span>{item.label}</span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <NotificationTableFooter
        linePerPage={linePerPage}
        totalData={totalData}
      />
    </div>
  ) : !isLoading && notificationsFiltered.length < 1 ? (
    <div className="tw-bg-white tw-text-[#001E64] tw-shadow-[0px_0px_5px_rgba(50,50,50,0.25)] tw-rounded-xl">
      <div className="tw-p-10 tw-flex tw-flex-col tw-gap-7 tw-justify-center tw-items-center">
        <img src={sadFaceIcon} alt="sad-face" width={75} height={75} />
        <p className="tw-text-[#AAA]">
          Parece que você não tem nenhuma notificação.
        </p>
      </div>
    </div>
  ) : (
    <div className="tw-bg-white text-[#001E64] tw-shadow-[0px_0px_5px_rgba(50,50,50,0.25)] tw-rounded-xl">
      <div className="tw-p-16 tw-flex tw-flex-col tw-gap-7 tw-justify-center tw-items-center">
        <div
          style={{ borderTopColor: 'transparent' }}
          className="tw-w-20 tw-h-20 tw-border-4 tw-border-[#001E64] tw-border-solid tw-rounded-full tw-animate-spin"
        ></div>
      </div>
    </div>
  );
};
